import { decodeJwtExpiryMs } from "./jwt";
import type { Session } from "./storage";

export type TokenBundle = {
  access_token: string;
  refresh_token: string;
};

export type User = {
  id: string;
  email: string;
  display_name: string | null;
  handle: string | null;
  created_at: string;
  is_admin?: boolean;
};

export type MagicLinkDelivery =
  | { sent: true }
  | { sent: false; reason: string };

export type Providers = {
  magic_link: boolean;
  google: boolean;
  microsoft: boolean;
};

// Discriminated outcomes for auth-relevant calls so callers can tell apart
// "server says no" (auth failure → re-auth required) from "network down"
// (offline → keep the session, retry later).
export type AuthCall<T> =
  | { ok: true; value: T }
  | { ok: false; kind: "auth"; status: number }
  | { ok: false; kind: "network" }
  | { ok: false; kind: "server"; status: number };

export function toSession(bundle: TokenBundle): Session {
  const exp = decodeJwtExpiryMs(bundle.access_token);
  return {
    access_token: bundle.access_token,
    refresh_token: bundle.refresh_token,
    // Fall back to 15-min if we can't decode `exp` — better to refresh too
    // often than to sit on a dead token.
    expires_at: exp ?? Date.now() + 15 * 60 * 1000,
  };
}

export async function fetchProviders(): Promise<Providers> {
  const res = await fetch("/api/auth/providers");
  if (!res.ok) throw new Error(`providers: HTTP ${res.status}`);
  return res.json() as Promise<Providers>;
}

export async function requestMagicLink(
  email: string,
  rememberDevice: boolean,
): Promise<{ delivery: MagicLinkDelivery }> {
  const res = await fetch("/api/auth/magic-link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, remember_device: rememberDevice }),
  });
  if (!res.ok) {
    throw new Error(`magic-link request failed: HTTP ${res.status}`);
  }
  return res.json() as Promise<{ delivery: MagicLinkDelivery }>;
}

/** OTP fallback for clients where the magic link can't share a session
 *  back to the installed app (iOS Safari → home-screen PWA).  The same
 *  email carries a 6-digit code that's validated against the unconsumed
 *  most-recent magic_link row for that email. */
export async function consumeMagicLinkCode(
  email: string,
  code: string,
): Promise<TokenBundle> {
  const res = await fetch("/api/auth/magic-link/consume-code", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: string }
      | null;
    throw new Error(body?.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<TokenBundle>;
}

export async function refreshSession(
  refreshToken: string,
): Promise<AuthCall<TokenBundle>> {
  let res: Response;
  try {
    res = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    return { ok: false, kind: "network" };
  }
  if (res.status === 401) return { ok: false, kind: "auth", status: 401 };
  if (!res.ok) return { ok: false, kind: "server", status: res.status };
  const value = (await res.json()) as TokenBundle;
  return { ok: true, value };
}

export async function logoutSession(refreshToken: string): Promise<void> {
  try {
    await fetch("/api/auth/logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // best-effort
  }
}

export async function fetchMe(accessToken: string): Promise<AuthCall<User>> {
  let res: Response;
  try {
    res = await fetch("/api/auth/me", {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch {
    return { ok: false, kind: "network" };
  }
  if (res.status === 401) return { ok: false, kind: "auth", status: 401 };
  if (!res.ok) return { ok: false, kind: "server", status: res.status };
  const value = (await res.json()) as User;
  return { ok: true, value };
}
